import { useState, useEffect } from 'react';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Rocket, Coins, Target, Users, Clock, TrendingUp,
  Loader2, Eye, UserPlus, Link2, Sparkles, CheckCircle2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';



// Duration options with credit costs
const DURATION_OPTIONS = [
  { key: '1_day', label: '1 Day', credits: 10, description: 'Quick visibility boost' },
  { key: '3_days', label: '3 Days', credits: 25, description: 'Most popular choice' },
  { key: '7_days', label: '7 Days', credits: 50, description: 'Extended reach' },
  { key: '14_days', label: '14 Days', credits: 90, description: 'Maximum impact' },
];

// Objective options
const OBJECTIVE_OPTIONS = [
  { key: 'more_views', label: 'More Views', icon: Eye, description: 'Get more people to see your post' },
  { key: 'more_profile_visits', label: 'Profile Visits', icon: Users, description: 'Drive traffic to your profile' },
  { key: 'more_followers', label: 'More Followers', icon: UserPlus, description: 'Grow your audience' },
  { key: 'more_engagement', label: 'Engagement', icon: TrendingUp, description: 'Get more likes & comments' },
];

export function BoostPostModal({ open, onOpenChange, postId, onBoostSuccess }) {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(0);
  const [selectedDuration, setSelectedDuration] = useState('3_days');
  const [selectedObjective, setSelectedObjective] = useState('more_views');
  const [targeting, setTargeting] = useState({
    roles: [],
    genres: [],
    states: []
  });
  const [success, setSuccess] = useState(false);

  // Fetch user's boost credits
  useEffect(() => {
    if (open) {
      fetchCredits();
      setSuccess(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCredits = async () => {
    try {
      const res = await fetch(`${API}/api/me/boost-credits`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCredits(data.boost_credits || 0);
      }
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    }
  };

  const selectedDurationOption = DURATION_OPTIONS.find(d => d.key === selectedDuration);
  const creditCost = selectedDurationOption?.credits || 0;
  const hasEnoughCredits = credits >= creditCost;

  const handleBoost = async () => {
    if (!hasEnoughCredits) {
      toast.error('Not enough boost credits');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/posts/${postId}/boost`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          duration: selectedDuration,
          objective: selectedObjective,
          targeting
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        setCredits(prev => prev - creditCost);
        toast.success('Post boosted successfully!');
        onBoostSuccess?.();
        
        setTimeout(() => {
          onOpenChange(false);
        }, 2000);
      } else {
        toast.error(data.detail || 'Failed to boost post');
      }
    } catch (err) {
      toast.error('Failed to boost post');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">Boost Activated!</h3>
            <p className="text-muted-foreground">
              Your post is now being promoted to more people.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" />
            Boost Post
          </DialogTitle>
          <DialogDescription>
            Promote your post to reach more people
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Credits Balance */}
          <Card className="p-4 bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Coins className="w-6 h-6 text-yellow-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Your Balance</p>
                  <p className="text-2xl font-bold">{credits} credits</p>
                </div>
              </div>
              <Badge variant={hasEnoughCredits ? "secondary" : "destructive"}>
                Cost: {creditCost} credits
              </Badge>
            </div>
          </Card>

          {/* Duration Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Boost Duration
            </Label>
            <RadioGroup value={selectedDuration} onValueChange={setSelectedDuration}>
              <div className="grid grid-cols-2 gap-2">
                {DURATION_OPTIONS.map(option => (
                  <label
                    key={option.key}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      selectedDuration === option.key 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-border"
                    )}
                  >
                    <RadioGroupItem value={option.key} className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{option.label}</span>
                        <Badge variant="outline" className="text-xs">
                          {option.credits} credits
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {option.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Objective Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Boost Objective
            </Label>
            <RadioGroup value={selectedObjective} onValueChange={setSelectedObjective}>
              <div className="grid grid-cols-2 gap-2">
                {OBJECTIVE_OPTIONS.map(option => {
                  const Icon = option.icon;
                  return (
                    <label
                      key={option.key}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedObjective === option.key 
                          ? "border-primary bg-primary/10" 
                          : "border-border hover:border-border"
                      )}
                    >
                      <RadioGroupItem value={option.key} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-primary" />
                          <span className="font-medium text-sm">{option.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {option.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </RadioGroup>
          </div>

          {/* Targeting Options (Optional) */}
          <Card className="p-4 border-border bg-muted/30">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Target Audience (Optional)
            </h4>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Target Roles</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {['Listener', 'Artist', 'Producer', 'DJ'].map(role => (
                    <label key={role} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox 
                        checked={targeting.roles.includes(role.toLowerCase())}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setTargeting(prev => ({
                              ...prev,
                              roles: [...prev.roles, role.toLowerCase()]
                            }));
                          } else {
                            setTargeting(prev => ({
                              ...prev,
                              roles: prev.roles.filter(r => r !== role.toLowerCase())
                            }));
                          }
                        }}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to target everyone
              </p>
            </div>
          </Card>

          {/* Insufficient Credits Warning */}
          {!hasEnoughCredits && (
            <Card className="p-4 border-red-500/20 bg-red-500/10">
              <p className="text-sm text-red-400">
                You need {creditCost - credits} more credits to boost this post.
                Contact an admin to receive boost credits.
              </p>
            </Card>
          )}

          {/* Boost Button */}
          <Button 
            className="w-full" 
            size="lg"
            disabled={!hasEnoughCredits || loading}
            onClick={handleBoost}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Boosting...' : `Boost for ${creditCost} Credits`}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Boosted posts appear as &quot;Sponsored&quot; in feeds
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BoostPostModal;
